/**
 * OverrideStatusDialog — admin-only quote status escape hatch (2026-07-24).
 *
 * Force the quote to any status except Converted (both directions — the
 * conversion service owns that status because it creates the work order).
 * Reason is mandatory and lands in a distinct 'StatusOverridden' QuoteEvent so
 * the activity log always shows who forced what, and why. Side effects are
 * preserved: overriding onto Declined/Withdrawn/Expired releases the schedule
 * (with the committed-work "Release & continue" confirm), onto Sent stamps
 * SentAt + locks FX, onto Accepted back-fills AcceptedAt.
 */
import { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import {
  overrideQuoteStatus,
  type StatusChangeResponse,
} from '../../../services/quoteService';
import type { QuoteStatus } from '../../../types/quote';

/**
 * Every status the override may target — mirrors the backend
 * OVERRIDABLE_STATUSES ('Converted' deliberately absent).
 */
const OVERRIDABLE_STATUSES: readonly QuoteStatus[] = [
  'Draft', 'Internal Review', 'Approved Internal', 'Rejected', 'Changes Requested',
  'Sent', 'Viewed', 'Accepted', 'Declined', 'Expired', 'Withdrawn',
];

/** Statuses whose transition releases the quote's schedule holds. */
const RELEASE_STATUSES: readonly QuoteStatus[] = ['Declined', 'Expired', 'Withdrawn'];

interface Props {
  open: boolean;
  quoteKey: string;
  currentStatus: QuoteStatus;
  onClose: () => void;
  /** Called after a successful override — the parent refetches + shows release info. */
  onOverridden: (result: StatusChangeResponse) => void;
}

/** Committed-work refusal details from the 409 SOURCE_RELEASES_COMMITTED body. */
interface CommittedWarning {
  committedBookings: number;
  taskNames: string[];
}

export const OverrideStatusDialog = ({ open, quoteKey, currentStatus, onClose, onOverridden }: Props) => {
  const [status, setStatus] = useState<QuoteStatus | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committedWarning, setCommittedWarning] = useState<CommittedWarning | null>(null);

  const targetOptions = OVERRIDABLE_STATUSES.filter((s) => s !== currentStatus);
  const releasesSchedule = status !== '' && RELEASE_STATUSES.includes(status);

  const reset = () => {
    setStatus(''); setReason('');
    setError(null); setCommittedWarning(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (releaseSchedule = false) => {
    if (!status || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await overrideQuoteStatus(quoteKey, {
        status,
        reason: reason.trim(),
      }, releaseSchedule ? { releaseSchedule: true } : undefined);
      reset();
      onOverridden(result);
    } catch (err) {
      const resp = (err as {
        response?: {
          status?: number;
          data?: { code?: string; error?: string; committedBookings?: number; taskNames?: string[] };
        };
      }).response;
      // P2-4 ceremony: committed work attached — confirm the release in place.
      if (resp?.status === 409 && resp.data?.code === 'SOURCE_RELEASES_COMMITTED') {
        setCommittedWarning({
          committedBookings: resp.data.committedBookings ?? 0,
          taskNames: resp.data.taskNames ?? [],
        });
      } else {
        setError(resp?.data?.error
          ?? (err instanceof Error ? err.message : 'Failed to override the quote status.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Override Quote Status</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 1 }}>
          Admin escape hatch — use the normal workflow buttons when they apply.
          The override is permanently recorded in the activity log with your
          name and reason.
        </Alert>
        <TextField label="Current status" fullWidth size="small" disabled
          value={currentStatus} sx={{ mt: 2 }} />
        <TextField label="New status" fullWidth required select size="small"
          value={status}
          onChange={(e) => { setStatus(e.target.value as QuoteStatus); setCommittedWarning(null); }}
          sx={{ mt: 2 }}>
          {targetOptions.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField label="Reason" fullWidth required multiline minRows={2} size="small"
          value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 2 }}
          placeholder="e.g. client approved by phone on 2026-07-24, quote was never sent from the system" />
        {releasesSchedule && !committedWarning && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Moving to {status} releases the quote's tentative schedule holds —
            machines and people become available again.
          </Alert>
        )}
        {committedWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Live schedule attached
            </Typography>
            <Typography variant="body2">
              Moving to {status} will release {committedWarning.committedBookings} committed
              booking(s) from the live schedule
              {committedWarning.taskNames.length > 0
                ? ` (${committedWarning.taskNames.join(', ')})`
                : ''}. This cannot be undone.
            </Typography>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {committedWarning ? (
          <Button variant="contained" color="error" onClick={() => handleSubmit(true)}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {submitting ? <CircularProgress size={18} /> : 'Release & continue'}
          </Button>
        ) : (
          <Button variant="contained" color="warning" onClick={() => handleSubmit(false)}
            disabled={submitting || !status || !reason.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {submitting ? <CircularProgress size={18} /> : 'Override Status'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
